/**
 * Drag-and-dock gesture for dees-mosaic tilebars: pointer capture with a
 * movement threshold, a fixed ghost chip, edge-quadrant drop targets with a
 * live preview, and Escape/blur cancel. The commit path goes through the
 * pure tree helpers; the component only re-renders from the host's next
 * layout.
 */
import type { DeesMosaic } from './dees-mosaic.js';
import type { TMosaicDropEdge } from './types.js';
import { mosaicMove } from './tree.js';

export interface IMosaicDropTarget {
  leafId: string;
  edge: TMosaicDropEdge;
  previewRect: DOMRect;
}

const dragThresholdPx = 4;

export class MosaicDragController {
  constructor(private host: DeesMosaic) {}

  private ghost: HTMLElement | null = null;
  private shield: HTMLElement | null = null;
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private blurListener: (() => void) | null = null;
  private detachGesture: (() => void) | null = null;
  private activeLeafId: string | null = null;

  /**
   * Cancels a gesture that is in flight, e.g. when the host element is
   * disconnected mid-drag — the pointer will never deliver its up/lost
   * events to a removed tilebar, so the window listeners must be detached
   * deterministically here.
   */
  public cancelActiveGesture(): void {
    this.detachGesture?.();
    if (this.ghost && this.activeLeafId !== null) {
      this.finishDrag(this.activeLeafId, false);
    }
    this.activeLeafId = null;
  }

  public handleTilebarPointerDown(event: PointerEvent, leafId: string, title: string): void {
    if (!this.host.dragEnabled || event.button !== 0) return;
    if (this.detachGesture) return; // one gesture at a time (multi-touch guard)
    const tilebar = event.currentTarget as HTMLElement;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    let started = false;

    const onMove = (moveEvent: PointerEvent) => {
      if (!started) {
        if (Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < dragThresholdPx) return;
        started = true;
        this.startDrag(tilebar, leafId, title);
      }
      this.updateDrag(moveEvent, leafId);
    };
    const cleanup = () => {
      this.detachGesture = null;
      tilebar.removeEventListener('pointermove', onMove);
      tilebar.removeEventListener('pointerup', onUp);
      tilebar.removeEventListener('lostpointercapture', onLost);
      try {
        tilebar.releasePointerCapture(pointerId);
      } catch {
        // not captured (synthetic pointer or already released)
      }
    };
    const onUp = () => {
      cleanup();
      if (started) this.finishDrag(leafId, true);
    };
    const onLost = () => {
      cleanup();
      if (started) this.finishDrag(leafId, false);
    };
    try {
      tilebar.setPointerCapture(pointerId);
    } catch {
      // synthetic pointers (tests) cannot be captured; listeners work anyway
    }
    this.detachGesture = cleanup;
    this.activeLeafId = leafId;
    tilebar.addEventListener('pointermove', onMove);
    tilebar.addEventListener('pointerup', onUp);
    tilebar.addEventListener('lostpointercapture', onLost);
  }

  private startDrag(tilebar: HTMLElement, leafId: string, title: string): void {
    tilebar.classList.add('dragging');
    const root = this.host.shadowRoot!.querySelector('.rootBox') as HTMLElement;
    this.shield = document.createElement('div');
    this.shield.className = 'dragShield';
    root.appendChild(this.shield);
    this.ghost = document.createElement('div');
    this.ghost.className = 'dragGhost';
    this.ghost.textContent = title;
    root.appendChild(this.ghost);
    this.keydownListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.finishDrag(leafId, false);
      }
    };
    this.blurListener = () => this.finishDrag(leafId, false);
    window.addEventListener('keydown', this.keydownListener, true);
    window.addEventListener('blur', this.blurListener);
    this.host.dispatchEvent(new CustomEvent('mosaic-drag-state', {
      detail: { dragging: true, leafId },
      bubbles: true,
      composed: true,
    }));
  }

  private updateDrag(event: PointerEvent, draggedLeafId: string): void {
    if (this.ghost) {
      this.ghost.style.left = `${event.clientX}px`;
      this.ghost.style.top = `${event.clientY}px`;
    }
    this.host.dropTarget = this.resolveDropTarget(event.clientX, event.clientY, draggedLeafId);
  }

  private resolveDropTarget(x: number, y: number, draggedLeafId: string): IMosaicDropTarget | null {
    const rects = this.host.getLeafRects();
    for (const [leafId, rect] of Object.entries(rects)) {
      if (leafId === draggedLeafId) continue;
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) continue;
      const distances: Array<[TMosaicDropEdge, number]> = [
        ['left', x - rect.left],
        ['right', rect.right - x],
        ['top', y - rect.top],
        ['bottom', rect.bottom - y],
      ];
      distances.sort((a, b) => a[1] - b[1]);
      const edge = distances[0][0];
      const previewRect = this.halfRect(rect, edge);
      return { leafId, edge, previewRect };
    }
    return null;
  }

  private halfRect(rect: DOMRect, edge: TMosaicDropEdge): DOMRect {
    if (edge === 'left') return new DOMRect(rect.left, rect.top, rect.width / 2, rect.height);
    if (edge === 'right') return new DOMRect(rect.left + rect.width / 2, rect.top, rect.width / 2, rect.height);
    if (edge === 'top') return new DOMRect(rect.left, rect.top, rect.width, rect.height / 2);
    return new DOMRect(rect.left, rect.top + rect.height / 2, rect.width, rect.height / 2);
  }

  private finishDrag(leafId: string, commit: boolean): void {
    // cancel paths (Escape/blur) arrive outside the pointer handlers: detach
    // the gesture listeners so a held pointer can no longer revive or commit
    // the cancelled drag on a later move/release
    this.detachGesture?.();
    this.activeLeafId = null;
    const target = this.host.dropTarget;
    this.host.dropTarget = null;
    for (const tilebar of this.host.shadowRoot?.querySelectorAll('.tilebar.dragging') ?? []) {
      tilebar.classList.remove('dragging');
    }
    this.ghost?.remove();
    this.ghost = null;
    this.shield?.remove();
    this.shield = null;
    if (this.keydownListener) {
      window.removeEventListener('keydown', this.keydownListener, true);
      this.keydownListener = null;
    }
    if (this.blurListener) {
      window.removeEventListener('blur', this.blurListener);
      this.blurListener = null;
    }
    this.host.dispatchEvent(new CustomEvent('mosaic-drag-state', {
      detail: { dragging: false, leafId },
      bubbles: true,
      composed: true,
    }));
    if (commit && target) {
      this.host.commitLayout(
        mosaicMove(this.host.layout, leafId, target.leafId, target.edge),
        'drop',
      );
    }
  }
}
